/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-undef */
import 'amazon-connect-streams';
import {
  API_PATIENTS_COMMENTS,
  API_READINGS_ACTIONS,
  API_CCM_PATIENT_CALL,
} from 'services/dataService/resources';
import { showResult } from 'infrastructure/hooks/utils';
import { SocketRoutes } from 'services/wsService';
import { differenceInSeconds } from 'date-fns';
import {
  emitCallEndedEvent,
  emitCallStartedEvent,
} from 'infrastructure/customEvents/callEvents';
import { ModuleType } from 'infrastructure/enums';

import dataService from '../dataService';

import type { TActionFrom } from 'infrastructure/interfaces/common';
import type WS from 'services/wsService';
import type { Orchestration } from 'aws-rum-web/dist/cjs/orchestration/Orchestration';

const AWS_CONNECT_URL_CCP = process.env.REACT_APP_CONNECT_URL_CCP!;
const AWS_CONNECT_URL = process.env.REACT_APP_CONNECT_URL!;

interface ConnectDetails {
  patientGuid: string;
  connectedAt?: Date;
}

const contactToPatientMap: Map<string, ConnectDetails> = new Map();

interface ConnectCallbacks {
  onCallStarted?: (
    callId: string,
    patientGuid: string,
    isRpmCall: boolean,
  ) => void;
  onCallEnded?: () => void;
}

interface IInitProps {
  phoneNumber: string;
  patientGuid: string;
  setCallId: (callId: string) => void;
  isRpmCall: boolean;
  deviceId: string;
  readingId: string;
  wsClient: typeof WS;
  awsRumAgent: Orchestration | null;
  isReadOnlyRole: boolean;
  actionFrom?: TActionFrom;
  callbacks?: ConnectCallbacks;
}

// Shared mutable props read by the single registered contact handler.
// Updated on every init() so the handler always registers calls under the
// patient whose chart is currently open — not a value frozen at registration.
let currentProps: IInitProps | null = null;
// Handle to the one live connect.contact() subscription, so terminate() can
// remove it. Registering more than one handler makes each fire onConnecting
// with its own patientGuid, spreading a single call across multiple patients.
let contactSubscription: connect.Subscription | null = null;
let ccpInitialized = false;

// ── Cross-tab call ownership ───────────────────────────────────────────────
// Amazon Connect runs ONE shared worker that syncs every contact to every open
// tab, so onConnecting fires in tabs that did NOT place the call. The SDK gives
// no "which tab dialed" signal — softphone dials happen inside the CCP iframe,
// which our outer window cannot observe. To register each call under exactly
// one patient we latch ownership at the earliest moment we do see the contact
// (onConnecting): the active/focused tab claims the contactId, broadcasts the
// claim to the other tabs, and that claim is frozen — later focus changes or a
// slower tab cannot re-register it. This works for repeat softphone calls in an
// already-open popup (no dependence on init() re-running) and prevents the
// duplicate cross-patient rows we saw with multiple open popups.
const CALL_OWNERSHIP_CHANNEL = 'connect-call-ownership';
let ownershipChannel: BroadcastChannel | null = null;
const contactsClaimedByOtherTab = new Set<string>();
// Set when THIS tab dials via agent.connect (below), consumed by the first
// onConnecting. The first call on a fresh CCP triggers an Amazon Connect login
// popup that steals focus, so document.hasFocus() is false when that call
// connects. This flag lets the dialing tab claim its own call regardless of
// focus. Repeat softphone redials don't go through agent.connect, so those
// still rely on focus — hence ownership is claimed on EITHER signal.
let hasPendingOutboundDial = false;

const getOwnershipChannel = (): BroadcastChannel => {
  if (!ownershipChannel) {
    ownershipChannel = new BroadcastChannel(CALL_OWNERSHIP_CHANNEL);
    ownershipChannel.onmessage = (e: MessageEvent<{ contactId?: string }>) => {
      if (e.data?.contactId) contactsClaimedByOtherTab.add(e.data.contactId);
    };
  }
  return ownershipChannel;
};

// Returns true if THIS tab should register the given contact. A tab owns the
// call if it dialed it (pending flag — survives the auth-redirect focus loss)
// OR it is the focused tab (covers softphone redials that skip agent.connect).
// The first claim wins and is broadcast so no other tab double-registers.
const claimCallOwnership = (contactId: string): boolean => {
  const channel = getOwnershipChannel();
  if (contactsClaimedByOtherTab.has(contactId)) return false;

  const dialedHere = hasPendingOutboundDial;
  hasPendingOutboundDial = false;
  // document.hasFocus() is false in every background tab, so only the tab the
  // nurse is looking at passes on focus. The dial flag covers the case where
  // the login popup stole focus from the originating tab on the first call.
  const focusedHere = typeof document !== 'undefined' && document.hasFocus();
  if (!dialedHere && !focusedHere) return false;

  channel.postMessage({ contactId });
  return true;
};

const init = (props: IInitProps) => {
  const { phoneNumber } = props;
  currentProps = props;

  const containerDiv = document.getElementById('connect-el')!;
  ccpInitialized = true;
  connect.core.initCCP(containerDiv, {
    ccpUrl: AWS_CONNECT_URL_CCP,
    loginUrl: AWS_CONNECT_URL,
    loginPopup: true,
    loginPopupAutoClose: true,
    region: 'us-east-1',
    pageOptions: {
      enableAudioDeviceSettings: false,
      enablePhoneTypeSettings: false,
    },
    softphone: {
      allowFramedSoftphone: true,
    },
  });

  if (phoneNumber) {
    connect.agent((agent) => {
      const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
      // Mark this tab as the dialer so it can claim the resulting call even if
      // the Amazon Connect login popup steals focus before the call connects.
      hasPendingOutboundDial = true;
      agent.connect(endpoint);
    });

    // Register the contact handler exactly once per CCP session. The handler
    // reads currentProps at event time (not a frozen closure), so every call
    // registers under whichever patient chart is open when it connects.
    // terminate() unsubscribes this handle; without that, reopening the modal
    // would stack handlers, each firing with its own stale patientGuid.
    if (!contactSubscription) {
      contactSubscription = connect.contact((contact) => {
        contact.onConnecting(async (event) => {
          if (event.isInbound()) return;

          const callProps = currentProps;
          if (!callProps || !callProps.patientGuid) return;

          const { contactId } = event;

          // Only the active tab (the one the nurse dialed from) registers this
          // call. Every other open popup receives the same event via the shared
          // worker and must skip it, or the call lands on the wrong patient.
          if (!claimCallOwnership(contactId)) return;

          callProps.setCallId(contactId);
          callProps.awsRumAgent?.recordEvent('callConnecting', {
            patientGuid: callProps.patientGuid,
            phoneNumber: callProps.phoneNumber,
            contactId,
          });

          let callData = {};
          callData = {
            patientGuid: callProps.patientGuid,
            actionFrom: callProps.actionFrom,
          };

          contactToPatientMap.set(contactId, {
            patientGuid: callProps.patientGuid,
          });
          callProps.wsClient.send(SocketRoutes.OnCallStarted, {
            patientGuid: callProps.patientGuid,
            isRpmCall: callProps.isRpmCall,
          });
          callProps.callbacks?.onCallStarted?.(
            contactId,
            callProps.patientGuid,
            callProps.isRpmCall,
          );

          if (callProps.isRpmCall) {
            callData = {
              ...callData,
              deviceId: callProps.deviceId,
              readingId: callProps.readingId,
              type: 'call',
              message: contactId,
              commentType: 'rpm',
            };
          } else {
            callData = {
              ...callData,
              callId: contactId,
            };
          }

          if (!callProps.isReadOnlyRole) {
            const { error } = await dataService.createOne(
              !callProps.isRpmCall
                ? API_CCM_PATIENT_CALL
                : callProps.deviceId && callProps.readingId
                ? API_READINGS_ACTIONS
                : API_PATIENTS_COMMENTS,
              callData,
            );

            if (error) showResult(error);
          }
        });

        contact.onConnected(async (event) => {
          if (event.isInbound()) return;
          const { contactId } = event;
          const currentPatient = contactToPatientMap.get(contactId);
          if (currentPatient) {
            currentPatient.connectedAt = new Date();
          }
          emitCallStartedEvent();
        });

        contact.onDestroy(async (event) => {
          if (event.isInbound()) return;
          const { contactId } = event;
          const currentPatientGuid =
            contactToPatientMap.get(contactId)?.patientGuid;
          if (currentPatientGuid) {
            const destroyProps = currentProps;
            destroyProps?.awsRumAgent?.recordEvent('callDestroyed', {
              patientGuid: currentPatientGuid,
              contactId,
            });
            destroyProps?.wsClient.send(SocketRoutes.OnCallEnded, {
              patientGuid: currentPatientGuid,
              isRpmCall: destroyProps?.isRpmCall,
            });
            destroyProps?.callbacks?.onCallEnded?.();
          }
        });

        contact.onEnded(async (event) => {
          if (event.isInbound()) return;

          const { contactId } = event;
          const currentPatient = contactToPatientMap.get(contactId);
          if (currentPatient) {
            const currentPatientGuid = currentPatient.patientGuid;
            const endedProps = currentProps;

            endedProps?.awsRumAgent?.recordEvent('callDestroyed', {
              patientGuid: currentPatientGuid,
              contactId,
            });
            endedProps?.wsClient.send(SocketRoutes.OnCallEnded, {
              patientGuid: currentPatientGuid,
              isRpmCall: endedProps?.isRpmCall,
            });
            endedProps?.callbacks?.onCallEnded?.();

            const callDurationSeconds = currentPatient.connectedAt
              ? differenceInSeconds(new Date(), currentPatient.connectedAt)
              : 0;
            emitCallEndedEvent({
              patientGuid: currentPatientGuid,
              callDuration: callDurationSeconds,
              module: endedProps?.isRpmCall ? ModuleType.RPM : ModuleType.CCM,
              callId: contactId,
            });
          }
        });
      });
    }
  }
};

const endActiveContact = () => {
  if (!ccpInitialized) return;
  try {
    connect.agent((agent) => {
      agent.getContacts().forEach((contact) => {
        const stateType = contact.getState().type;
        if (stateType === connect.ContactStateType.CONNECTING) {
          contact.getAgentConnection()?.destroy();
        } else if (stateType === connect.ContactStateType.ENDED) {
          contact.destroy();
        }
        // CONNECTED (live call) is left untouched
      });
    });
  } catch {
    // SDK may throw if CCP is not in a connectable state
  }
};

const terminate = () => {
  connect.core.terminate();
  // Explicitly remove the contact handler so the next init() registers a
  // single fresh one. connect.core.terminate() also unsubscribes it via the
  // event bus, but dropping our own handle keeps the two in sync and prevents
  // a stale handler from ever firing with an outdated patientGuid.
  contactSubscription?.unsubscribe();
  contactSubscription = null;
  currentProps = null;
  ccpInitialized = false;
  // Close this tab's ownership channel; other tabs keep their own open. The
  // claimed-contact set is per-tab session state and is dropped with it.
  ownershipChannel?.close();
  ownershipChannel = null;
  contactsClaimedByOtherTab.clear();
  // Clear any dial that never connected, so it can't claim a later call.
  hasPendingOutboundDial = false;
  const containerDiv = document.getElementById('connect-el');
  if (containerDiv) {
    const iframe = containerDiv.firstElementChild;
    if (iframe) containerDiv.removeChild(iframe);
  }
};

export { init, terminate, endActiveContact };
